import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { computeEstimatesList } from "../../api/quotations/index.ts";
import {
  formatCurrency,
  currencyFormatterMXN,
} from "../../services/valueFormatters.js";
import Icon from "@mui/material/Icon";

interface QuotingQuantitiesCSVProps {
  props: any;
}

interface CostData {
  packingCost: number[];
  rawMaterial: number[];
  startingCost: number[];
  operatingCost: number[];
  utility: number[];
  sellerCommission: number[];
  totalCost: number[];
}

const QuotingQuantitiesCSV: React.FC<QuotingQuantitiesCSVProps> = ({
  props,
}) => {
  const { id } = props;
  const [quantities, setQuantities] = useState<string[]>([""]);
  const [isLoading, setIsLoading] = useState(false);
  const [plotLoading, setPlotLoading] = useState(false);
  const [costData, setCostData] = useState<CostData>({
    packingCost: [],
    rawMaterial: [],
    startingCost: [],
    operatingCost: [],
    utility: [],
    sellerCommission: [],
    totalCost: [],
  });
  const [plotQuantities, setPlotQuantities] = useState<number[]>([]);

  const updatePlotData = async (validQuantities: number[]) => {
    if (validQuantities.length === 0) {
      setCostData({
        packingCost: [],
        rawMaterial: [],
        startingCost: [],
        operatingCost: [],
        utility: [],
        sellerCommission: [],
        totalCost: [],
      });
      setPlotQuantities([]);
      return;
    }

    try {
      setPlotLoading(true);
      const response = await computeEstimatesList(id, validQuantities);
      const result = response.data;

      // Extract cost components and calculate unit costs
      const packingCost = result.map(
        (item) => item.packingCost / validQuantities[result.indexOf(item)]
      );
      const rawMaterial = result.map(
        (item) => item.rawMaterialCost / validQuantities[result.indexOf(item)]
      );
      const startingCost = result.map(
        (item) => item.startingCost / validQuantities[result.indexOf(item)]
      );
      const operatingCost = result.map(
        (item) => item.operatingCost / validQuantities[result.indexOf(item)]
      );
      const utility = result.map(
        (item) => item.utility / validQuantities[result.indexOf(item)]
      );
      const sellerCommission = result.map(
        (item) => item.sellerCommission / validQuantities[result.indexOf(item)]
      );
      const totalCost = result.map(
        (item) => item.totalCost / validQuantities[result.indexOf(item)]
      );

      setCostData({
        packingCost,
        rawMaterial,
        startingCost,
        operatingCost,
        utility,
        sellerCommission,
        totalCost,
      });
      setPlotQuantities(validQuantities);
    } catch (error) {
      console.error("Error fetching plot data:", error);
    } finally {
      setPlotLoading(false);
    }
  };

  const handleAddQuantity = () => {
    setQuantities([...quantities, ""]);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const newQuantities = [...quantities];
    newQuantities[index] = value;
    setQuantities(newQuantities);
  };

  // Update plot when quantities change
  useEffect(() => {
    const validQuantities = quantities
      .map((q) => parseFloat(q))
      .filter((q) => !isNaN(q) && q > 0)
      .sort((a, b) => a - b);
    
    const timeoutId = setTimeout(() => {
      updatePlotData(validQuantities);
    }, 500); // Debounce to avoid too many API calls

    return () => clearTimeout(timeoutId);
  }, [quantities, id]);

  const handleRemoveQuantity = (index: number) => {
    const newQuantities = quantities.filter((_, i) => i !== index);
    setQuantities(newQuantities);
  };

  const generateCSV = (data: any[], quantities: number[]) => {
    if (!data.length) return;
    const itemNames = Array.from(
      new Set(data.flatMap((q) => q.items.map((item: any) => item.name)))
    );
    const itemUnitCosts: Record<string, (string | number)[]> = {};
    itemNames.forEach((name) => {
      itemUnitCosts[name] = quantities.map((qty, i) => {
        const qData = data.find((d) => d.quantity === qty);
        if (!qData) return "";
        const item = qData.items.find((it: any) => it.name === name);
        if (!item) return "";
        return (item.totalCost / qty).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        });
      });
    });

    // Add packing cost per quantity
    const packingCosts = quantities.map((qty) => {
      const qData = data.find((d) => d.quantity === qty);
      if (!qData) return "";
      return (qData.packingCost / qty).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
    });

    // Calculate BOM (sum of every component + packing cost)
    const bomCosts = quantities.map((qty) => {
      const qData = data.find((d) => d.quantity === qty);
      if (!qData) return "";
      const componentSum = qData.items.reduce((sum: number, item: any) => sum + item.totalCost, 0);
      const bomTotal = (componentSum + qData.packingCost) / qty;
      return bomTotal.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
    });

    // Calculate Utility (BOM * 1.5)
    const utilityCosts = quantities.map((qty) => {
      const qData = data.find((d) => d.quantity === qty);
      if (!qData) return "";
      const componentSum = qData.items.reduce((sum: number, item: any) => sum + item.totalCost, 0);
      const bomTotal = (componentSum + qData.packingCost) / qty;
      const utility = bomTotal * 0.5;
      return utility.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
    });

    // Calculate Seller Commission ((BOM + Utility) * 0.3)
    const sellerCommissionCosts = quantities.map((qty) => {
      const qData = data.find((d) => d.quantity === qty);
      if (!qData) return "";
      const componentSum = qData.items.reduce((sum: number, item: any) => sum + item.totalCost, 0);
      const bomTotal = (componentSum + qData.packingCost) / qty;
      const utility = bomTotal * 0.5;
      const sellerCommission = (bomTotal + utility) * 0.03;
      return sellerCommission.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
    });

    // Calculate Total (BOM + Utility + Seller Commission)
    const totalCosts = quantities.map((qty) => {
      const qData = data.find((d) => d.quantity === qty);
      if (!qData) return "";
      const componentSum = qData.items.reduce((sum: number, item: any) => sum + item.totalCost, 0);
      const bomTotal = (componentSum + qData.packingCost) / qty;
      const utility = bomTotal * 0.5;
      const sellerCommission = (bomTotal + utility) * 0.03;
      const total = bomTotal + utility + sellerCommission;
      return total.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 2,
      });
    });

    const headers = ["Component", ...quantities.map((q) => q.toLocaleString())];
    const csvContent = [
      headers.join(","),
      ...itemNames.map((name) => [name, ...itemUnitCosts[name]].join(",")),
      ["Empaque", ...packingCosts].join(","),
      ["BOM", ...bomCosts].join(","),
      ["Utilidad", ...utilityCosts].join(","),
      ["Comisión Vendedor", ...sellerCommissionCosts].join(","),
      ["Total", ...totalCosts].join(","),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `quotation_${id}_costs.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateCSV = async () => {
    try {
      setIsLoading(true);
      const validQuantities = quantities
        .map((q) => parseFloat(q))
        .filter((q) => !isNaN(q));
      if (validQuantities.length === 0) {
        alert("Please enter at least one valid quantity");
        return;
      }
      const response = await computeEstimatesList(id, validQuantities);
      generateCSV(response.data, validQuantities);
    } catch (error) {
      console.error("Error generating CSV:", error);
      alert("Error generating CSV. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={2} item xs={12}>
      <Grid item xs={12} pb={2}>
        <Typography variant="h4">
          Crear Reporte de Costos por Cantidad CSV
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <Box>
          <Grid container spacing={2}>
            {quantities.map((quantity, index) => (
              <Grid container spacing={2} key={index} pl={2}>
                <Grid item xs={11} pb={2}>
                  <TextField
                    fullWidth
                    label={`Cantidad`}
                    type="number"
                    value={quantity}
                    onChange={(e) =>
                      handleQuantityChange(index, e.target.value)
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={1}>
                  <IconButton
                    onClick={() => handleRemoveQuantity(index)}
                    color="error"
                    disabled={quantities.length === 1}
                  >
                    <Icon>delete</Icon>
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddQuantity}
                color="secondary"
              >
                <Icon>add</Icon>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      
      {/* Plot Section */}
      <Grid item xs={9}>
        <Box>
          {plotLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress />
            </Box>
          ) : plotQuantities.length > 0 ? (
            <Box sx={{ width: "100%", height: 400 }}>
              <LineChart
                xAxis={[
                  {
                    scaleType: "point",
                    data: plotQuantities.map(q => q.toLocaleString()),
                    label: "Cantidad",
                  },
                ]}
                yAxis={[
                  {
                    ...currencyFormatterMXN,
                  },
                ]}
                series={[
                  {
                    ...currencyFormatterMXN,
                    data: costData.totalCost,
                    label: "Total",
                    area: true,
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.startingCost,
                    label: "Costo de Arranque",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.rawMaterial,
                    label: "Materia Prima",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.operatingCost,
                    label: "Costo Operativo",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.packingCost,
                    label: "Costo de Empaque",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.utility,
                    label: "Utilidad",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                  {
                    ...currencyFormatterMXN,
                    data: costData.sellerCommission,
                    label: "Comisión Vendedor",
                    area: true,
                    stack: "total",
                    showMark: true,
                  },
                ]}
                margin={{ top: 40, right: 40, bottom: 60, left: 100 }}
                legend={{
                  direction: "column",
                  position: { vertical: "top", horizontal: "right" },
                }}
                sx={{
                  ".MuiChartsLegend-root": {
                    background: "rgba(255,255,255,0.85)",
                    borderRadius: 2,
                    boxShadow: 1,
                    zIndex: 2,
                  },
                  ".MuiChartsAxis-left .MuiChartsAxis-tickLabel": {
                    fontSize: 14,
                  },
                  ".MuiChartsAxis-left": {
                    zIndex: 3,
                  },
                  ".MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
                    fontSize: 14,
                  },
                }}
              />
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <Typography color="text.secondary">
                Ingresa cantidades válidas para ver el gráfico
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          onClick={handleGenerateCSV}
          disabled={isLoading}
          color="primary"
        >
          {isLoading ? "Generando..." : "Generar CSV"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default QuotingQuantitiesCSV;
